import React, { useEffect, useState, memo } from 'react';
import { HeartIcon, SendIcon, StarIcon } from 'lucide-react';
interface Review {
  id: string;
  name: string;
  text: string;
  rating: number;
  likes: number;
  timeAgo: string;
}
const SEED_REVIEWS: Review[] = [{
  id: 's1',
  name: '@sarah_momof2',
  text: 'My son finally finishes his activities without melting down. We do 10 minutes every morning and his focus has completely changed. Worth every penny. ❤️',
  rating: 5,
  likes: 47,
  timeAgo: '2 min ago'
}, {
  id: 's2',
  name: '@the_miller_family',
  text: 'The best part is zero screens and zero prep. I just print a page and he is instantly engaged. His memory games are his favorite.',
  rating: 5,
  likes: 38,
  timeAgo: '8 min ago'
}, {
  id: 's3',
  name: '@dad_of_twins',
  text: 'I was skeptical about a printable, but the difference in my daughter\'s confidence is real. She actually asks to do "brain time" now.',
  rating: 5,
  likes: 92,
  timeAgo: '15 min ago'
}, {
  id: 's4',
  name: '@jess_and_liam',
  text: 'My 5-year-old used to give up the second something got hard. After a few weeks he pushes through puzzles on his own. Huge win for us.',
  rating: 5,
  likes: 56,
  timeAgo: '23 min ago'
}, {
  id: 's5',
  name: '@momma.bear.emily',
  text: 'The Calm & Self-Control volume has been a game changer for tantrums. The breathing activities actually work. I cried a little, honestly.',
  rating: 5,
  likes: 134,
  timeAgo: '31 min ago'
}, {
  id: 's6',
  name: '@rachel_specialneeds',
  text: 'My son has ADHD and nothing held his attention like this. He feels capable for the first time. That alone is priceless.',
  rating: 5,
  likes: 78,
  timeAgo: '42 min ago'
}, {
  id: 's7',
  name: '@homeschool_hannah',
  text: 'I homeschool three kids and having structured brain activities ready to print saves my sanity. Each one picks their level. Brilliant.',
  rating: 5,
  likes: 41,
  timeAgo: '1 h ago'
}, {
  id: 's8',
  name: '@david.k',
  text: 'I come home tired from work, print one page, and my son is excited to do it with me. No planning needed. We both look forward to it.',
  rating: 5,
  likes: 29,
  timeAgo: '1 h ago'
}, {
  id: 's9',
  name: '@grandma_olivia',
  text: 'I bought this for my grandkids. Family Brain Night has become our favorite tradition. So much laughing and learning together. 🥺',
  rating: 5,
  likes: 167,
  timeAgo: '2 h ago'
}, {
  id: 's10',
  name: '@megan.mom.of.dos',
  text: 'I recommend this to every parent I know. The detective and escape room bonuses keep my daughter busy for hours — screen-free.',
  rating: 5,
  likes: 53,
  timeAgo: '3 h ago'
}, {
  id: 's11',
  name: '@the_brooks_house',
  text: "Six months in and the difference in my son's reading and reasoning is obvious. His teacher even noticed. Worth ten times the price.",
  rating: 5,
  likes: 84,
  timeAgo: '4 h ago'
}, {
  id: 's12',
  name: '@ryan_dad',
  text: 'I get my kids on weekends and this gave us a real routine. They ask for their brain pages every Saturday morning now. Lifesaver.',
  rating: 5,
  likes: 198,
  timeAgo: '5 h ago'
}];
const STORAGE_KEY = 'littlegenius_reviews_v1';
const LIKES_KEY = 'littlegenius_likes_v1';
export function CommunityReviewsSection() {
  const [reviews, setReviews] = useState<Review[]>(SEED_REVIEWS);
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  // Load saved state
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as Review[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          setReviews([...parsed, ...SEED_REVIEWS].slice(0, 15));
        }
      }
      const liked = localStorage.getItem(LIKES_KEY);
      if (liked) {
        setLikedIds(new Set(JSON.parse(liked) as string[]));
      }
    } catch {
      // ignore
    }
  }, []);
  const persistUserReviews = (userReviews: Review[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(userReviews));
    } catch {
      // ignore
    }
  };
  const persistLikes = (next: Set<string>) => {
    try {
      localStorage.setItem(LIKES_KEY, JSON.stringify(Array.from(next)));
    } catch {
      // ignore
    }
  };
  const handleLike = (id: string) => {
    setReviews(prev => prev.map(r => {
      if (r.id !== id) return r;
      const isLiked = likedIds.has(id);
      return {
        ...r,
        likes: r.likes + (isLiked ? -1 : 1)
      };
    }));
    setLikedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      persistLikes(next);
      return next;
    });
  };
  const handleSubmit = () => {
    if (!text.trim() || rating === 0) return;
    if (reviews.length >= 15) {
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
      return;
    }
    const newReview: Review = {
      id: `u${Date.now()}`,
      name: name.trim() || 'Anonymous family',
      text: text.trim(),
      rating,
      likes: 0,
      timeAgo: 'a few seconds ago'
    };
    const updated = [newReview, ...reviews].slice(0, 15);
    setReviews(updated);
    // Persist only the user-added ones (those with id starting with 'u')
    const userOnly = updated.filter(r => r.id.startsWith('u'));
    persistUserReviews(userOnly);
    setText('');
    setName('');
    setRating(0);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };
  const canSubmit = text.trim().length > 0 && rating > 0 && reviews.length < 15;
  const visibleReviews = reviews.slice(0, visibleCount);
  const hasMore = visibleCount < reviews.length;
  return <section className="py-12 md:py-20 px-4 bg-black text-white" data-id="element-54">
      <div className="max-w-3xl mx-auto" data-id="element-55">
        <div className="text-center mb-8 md:mb-10" data-id="element-56">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-3 md:mb-4" data-id="element-57">
            ❤️ What Families Are Saying
          </h2>
          <p className="text-base md:text-lg text-gray-400 font-medium" data-id="element-58">
            Over 12,000 families worldwide are building stronger brains with The
            Little Genius System™
          </p>
          <div className="flex items-center justify-center gap-2 mt-3" data-id="element-59">
            <div className="flex" data-id="element-60">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 md:w-6 md:h-6 fill-yellow-400 text-yellow-400" data-id="element-61" />)}
            </div>
            <span className="text-sm md:text-base text-gray-300 font-bold" data-id="element-62">
              4.9 / 5
            </span>
            <span className="text-xs md:text-sm text-gray-500" data-id="element-63">
              · {reviews.length} reviews
            </span>
          </div>
        </div>

        {/* Review form */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 md:p-7 mb-6 md:mb-8 shadow-xl" data-id="element-64">
          <h3 className="text-base md:text-lg font-black text-white mb-4" data-id="element-65">
            Leave your review for the community
          </h3>

          <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Write your experience with The Little Genius System..." className="w-full bg-gray-950 border border-gray-700 rounded-xl p-3 md:p-4 text-sm md:text-base text-white placeholder-gray-500 mb-4 focus:outline-none focus:border-yellow-400 resize-none" rows={4} maxLength={400} data-id="element-66" />

          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 mb-4" data-id="element-67">
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name (optional)" maxLength={40} className="flex-1 bg-gray-950 border border-gray-700 rounded-xl px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400" data-id="element-68" />

            <div className="flex items-center gap-2" data-id="element-69">
              <span className="text-sm md:text-base text-gray-400 font-medium" data-id="element-70">
                Rate:
              </span>
              <div className="flex gap-1" data-id="element-71">
                {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => setRating(star)} onMouseEnter={() => setHoverRating(star)} onMouseLeave={() => setHoverRating(0)} className="cursor-pointer transition-transform hover:scale-110" aria-label={`${star} stars`} data-id="element-72">
                    <StarIcon className={`w-6 h-6 md:w-7 md:h-7 transition-colors ${star <= (hoverRating || rating) ? 'fill-yellow-400 text-yellow-400' : 'fill-transparent text-gray-600'}`} data-id="element-73" />
                  </button>)}
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-id="element-74">
            <button onClick={handleSubmit} disabled={!canSubmit} className={`inline-flex items-center justify-center gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-xl font-black text-sm md:text-base uppercase tracking-wide transition-all ${canSubmit ? 'bg-yellow-400 text-black hover:bg-yellow-300 cursor-pointer' : 'bg-gray-800 text-gray-500 cursor-not-allowed'}`} data-id="element-75">
              <SendIcon className="w-4 h-4 md:w-5 md:h-5" data-id="element-76" />
              POST REVIEW
            </button>

            {reviews.length >= 15 && <p className="text-xs md:text-sm text-yellow-400 font-medium" data-id="element-77">
                Review limit of 15 reached
              </p>}
          </div>

          {submitted && <p className="text-green-400 text-sm font-bold mt-3" data-id="element-78">
              ✅ Thank you for sharing your experience!
            </p>}
        </div>

        {/* Reviews list */}
        <div className="space-y-4" data-id="element-79">
          {visibleReviews.map(review => {
          const initial = review.name.replace('@', '').charAt(0).toUpperCase();
          const isLiked = likedIds.has(review.id);
          return <div key={review.id} className="bg-gray-900 border border-gray-800 rounded-2xl p-5 md:p-6" data-id="element-80">
                <div className="flex items-start justify-between mb-3 gap-3" data-id="element-81">
                  <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-82">
                    <div className="w-10 h-10 md:w-11 md:h-11 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-83">
                      <span className="text-black font-black text-sm md:text-base" data-id="element-84">
                        {initial}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-85">
                      <p className="text-sm md:text-base font-bold text-white truncate" data-id="element-86">
                        {review.name}
                      </p>
                      <p className="text-[10px] md:text-xs text-gray-500" data-id="element-87">
                        {review.timeAgo}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-shrink-0" data-id="element-88">
                    {[...Array(5)].map((_, idx) => <StarIcon key={idx} className={`w-4 h-4 md:w-5 md:h-5 ${idx < review.rating ? 'fill-yellow-400 text-yellow-400' : 'fill-transparent text-gray-600'}`} data-id="element-89" />)}
                  </div>
                </div>
                <p className="text-sm md:text-base text-gray-300 font-medium leading-relaxed mb-4" data-id="element-90">
                  {review.text}
                </p>
                <div className="flex items-center" data-id="element-91">
                  <button onClick={() => handleLike(review.id)} className={`inline-flex items-center gap-2 text-sm font-bold transition-colors cursor-pointer ${isLiked ? 'text-pink-400' : 'text-gray-500 hover:text-pink-400'}`} aria-label={isLiked ? 'Unlike' : 'Like'} data-id="element-92">
                    <HeartIcon className={`w-5 h-5 transition-all ${isLiked ? 'fill-pink-400 text-pink-400 scale-110' : 'fill-transparent'}`} data-id="element-93" />
                    <span data-id="element-94">{review.likes}</span>
                  </button>
                </div>
              </div>;
        })}
        </div>

        {hasMore && <div className="text-center mt-6 md:mt-8" data-id="element-95">
            <button onClick={() => setVisibleCount(c => Math.min(c + 5, reviews.length))} className="inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-xl border border-gray-700 bg-gray-900 hover:bg-gray-800 text-white font-bold text-sm md:text-base transition-colors cursor-pointer" data-id="element-96">
              Show more reviews ({reviews.length - visibleCount})
            </button>
          </div>}
      </div>
    </section>;
}